import {
    type ImageSizeFragment,
    type MonolithImageSizeFragment,
} from '@/graphql/api'
import type { ImageProps } from '@/components/Image.vue'
import type { Breakpoint } from '@/types/breakpoint.ts'

export const imageSizes = {
    W250: 250,
    W320: 320,
    W480: 480,
    W640: 640,
    W768: 768,
    W1024: 1024,
    W1280: 1280,
    W1440: 1440,
    W1920: 1920,
    W2560: 2560,
} as const

export type ImageSizeKey = keyof typeof imageSizes
export type ImageSizeValue = (typeof imageSizes)[ImageSizeKey]

export type GenericImageSource = {
    width?: number | null
    url: string
    alt?: string | null
    sizes: (MonolithImageSizeFragment | ImageSizeFragment)[]
}

type ImagesByFormat = {
    format: string
    images: ImageSource[]
}

type ImageSource = {
    width: number
    src: string
}

type ImageSize = [Breakpoint, string]
export type ImageSizes = [string, ...ImageSize[]]

export function getImageProps(
    input: GenericImageSource,
    sizes?: ImageSizes
): Pick<ImageProps, 'src' | 'sources'> {
    let largest: MonolithImageSizeFragment | ImageSizeFragment | undefined

    const width = input.width

    const byFormat = input.sizes.reduce(
        (acc, curr) => {
            const size = curr.width

            if (!size) {
                return acc
            }

            if (width && size && size > width) {
                return acc
            }

            largest = curr

            curr.sources.forEach((src) => {
                const mime = src.mimeType

                if (!(mime in acc)) {
                    acc[mime] = {
                        format: mime,
                        images: [],
                    }
                }

                const inFormat = acc[mime]!

                inFormat.images.push({
                    src: src.url,
                    width: size,
                })
            })

            return acc
        },
        {} as Record<string, ImagesByFormat>
    )

    return {
        src: largest?.url || input.url,
        sources: Object.entries(byFormat).map(([mimeType, images]) => {
            const srcset = images.images
                .map((image) => {
                    return `${image.src} ${image.width}w`
                })
                .join(', ')

            return {
                mimeType,
                srcset,
                // !! DO NOT EVER DEFAULT TO AUTO
                sizes: parseSizes(sizes),
            }
        }),
    }
}

export function parseSizes(sizes?: ImageSizes): string | undefined {
    if (undefined === sizes) {
        return undefined
    }

    if (sizes.length === 0) {
        return undefined
    }

    return (
        sizes
            .map((size) => {
                if (typeof size === 'string') {
                    return size
                }

                return `(min-width: ${size[0]}px) ${size[1]}`
            })
            // Do not use sizes.toReversed(), errors in browsers
            // Also, do not mutate sizes, it will cause a mess
            .reverse()
            .join(', ')
    )
}
